import { useLoading } from '@/providers/LoadingProvider';
import React, { ComponentType, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import Editor, { createEditorStateWithText } from '@draft-js-plugins/editor';
import { AtomicBlockUtils, convertToRaw, EditorState } from 'draft-js';
import { convertToHTML } from 'draft-convert';
import draftToHtml from 'draftjs-to-html';
import createMentionPlugin, { defaultSuggestionsFilter, MentionData } from '@draft-js-plugins/mention';
import createLinkPlugin from '@draft-js-plugins/anchor';
import createLinkifyPlugin from '@draft-js-plugins/linkify';
import createImagePlugin from '@draft-js-plugins/image';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { fileToBase64, useBase64FromFileInput } from '@/hooks/useBase64FromFileInput';
import { useCreateCommentImage } from '@/hooks/api/2150_create_comment_image';
import { BlockquoteButton, BoldButton, CodeBlockButton, ItalicButton, UnderlineButton } from '@draft-js-plugins/buttons';
import classes from '@/assets/scss/draft-editor.module.scss';
import toolbarStyle from '@/assets/scss/draft-toolbar.module.scss';
import toolbarButtonStyle from '@/assets/scss/draft-toolbar-button.module.scss';
import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import '@draft-js-plugins/mention/lib/plugin.css';
import '@draft-js-plugins/anchor/lib/plugin.css';
import '@draft-js-plugins/linkify/lib/plugin.css';
import '@/assets/scss/draft-editor-custom.scss';
import { toastError } from '@/lib/toast';

type Props = {
  mentions: MentionData[];
  editorState: EditorState;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
};

// LinkButton bugfix https://github.com/draft-js-plugins/draft-js-plugins/issues/1802
interface OverrideContentProps {
  getEditorState: () => EditorState;
  setEditorState: (editorState: EditorState) => void;
  onOverrideContent: (content: ComponentType<unknown> | undefined) => void;
}
type OverrideOnOverrideContent = (content: ComponentType<OverrideContentProps> | undefined) => void;

export const DraftEditor = ({ mentions, editorState, setEditorState }: Props) => {
  const { setShowLoading } = useLoading();

  // useEffect(() => {
  //   // console.log(editorState.getCurrentContent());
  //   console.log(editorState.getCurrentContent().getPlainText());
  //   // console.log(convertToRaw(editorState.getCurrentContent()));
  //
  //   console.log(
  //     convertToHTML({
  //       entityToHTML: (entity, originalText) => {
  //         if (entity.type == 'LINK') {
  //           return <a href={entity.data.url}>{originalText}</a>;
  //         } else if (entity.type == 'image') {
  //           return <img src={entity.data.src} />;
  //         }
  //         return originalText;
  //       },
  //     })(editorState.getCurrentContent())
  //   );
  //
  //   const rawData = JSON.stringify(convertToRaw(editorState.getCurrentContent()));
  //   console.log(rawData);
  // }, [editorState]);

  let editorRef = useRef<Editor>(null);

  const [open, setOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState(mentions);

  const { MentionSuggestions, InlineToolbar, LinkButton, plugins } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({
      mentionPrefix: '@',
    });
    const { MentionSuggestions } = mentionPlugin;

    const linkPlugin = createLinkPlugin();
    const { LinkButton } = linkPlugin;

    const linkifyPlugin = createLinkifyPlugin();

    const imagePlugin = createImagePlugin();

    const inlineToolbarPlugin = createInlineToolbarPlugin({ theme: { toolbarStyles: toolbarStyle, buttonStyles: toolbarButtonStyle } });
    const { InlineToolbar } = inlineToolbarPlugin;

    const plugins = [inlineToolbarPlugin, mentionPlugin, linkPlugin, imagePlugin, linkifyPlugin];
    return { plugins, InlineToolbar, LinkButton, MentionSuggestions };
  }, []);

  const handleSearchChange = ({ value }: { value: string }) => setSuggestions(defaultSuggestionsFilter(value, mentions));
  const handleChange = (s: EditorState) => setEditorState(s);

  return (
    <>
      <div className={classes.editor}>
        <Editor ref={editorRef} editorState={editorState} onChange={handleChange} plugins={plugins} />
        <InlineToolbar>
          {(externalProps) => (
            <>
              <BoldButton {...externalProps} />
              <ItalicButton {...externalProps} />
              <UnderlineButton {...externalProps} />
              <BlockquoteButton {...externalProps} />
              <LinkButton theme={externalProps.theme} onOverrideContent={externalProps.onOverrideContent as OverrideOnOverrideContent} />
            </>
          )}
        </InlineToolbar>
        <MentionSuggestions open={open} onOpenChange={setOpen} suggestions={suggestions} onSearchChange={handleSearchChange} onAddMention={(m) => {}} />
      </div>
    </>
  );
};
